<script setup lang="ts">
    import { reactive, computed, onMounted } from 'vue';
    import Loading from '@shared/components/Loading.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import ConfirmButton from '@shared/components/ConfirmButton.vue';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { getTags, createTag, updateTag, deleteTag, mergeTags, type TagSummary } from './AccountTagManager.access';

    const state = reactive<{
        isLoading: boolean;
        tags: TagSummary[];
        title: string;
        isCreating: boolean;
        isEditMode: boolean;
        selectedTagIds: string[];
        isDeleting: boolean;
        isMerging: boolean;
    }>({
        isLoading: true,
        tags: [],
        title: '',
        isCreating: false,
        isEditMode: false,
        selectedTagIds: [],
        isDeleting: false,
        isMerging: false,
    });

    onMounted(async () => {
        state.tags = await getTags();
        state.isLoading = false;
    });

    const selectedTags = computed(() => state.tags.filter((tag) => state.selectedTagIds.includes(tag.TagId)));

    function isSelected(tag: TagSummary): boolean {
        return state.selectedTagIds.includes(tag.TagId);
    }

    function toggleSelectedTag(tag: TagSummary): void {
        const index = state.selectedTagIds.indexOf(tag.TagId);
        index === -1 ? state.selectedTagIds.push(tag.TagId) : state.selectedTagIds.splice(index, 1);
    }

    function clearSelectedTags(): void {
        state.selectedTagIds = [];
    }

    function toggleEditMode(newVal: boolean): void {
        state.isEditMode = newVal;
        clearSelectedTags();
    }

    async function submitCreateTag(): Promise<void> {
        if (!state.title || isBusy.value) return;

        state.isCreating = true;

        try {
            const tag = await createTag(state.title);
            state.tags.push(tag);
            state.title = '';
            bsAlert({ type: AlertTypes.Success, content: $Resources.TagManagerAlertCreateTagBody });
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.isCreating = false;
        }
    }

    function disableNewLine(event: KeyboardEvent): void {
        if (event.key === 'Enter') event.preventDefault();
    }

    async function onTitleBlur(tag: TagSummary, event: FocusEvent): Promise<void> {
        const target = event.target as HTMLElement;
        const newTitle = target.innerText;

        if (newTitle === tag.Title) return;

        if (newTitle.length === 0) {
            target.innerText = tag.Title;
            return;
        }

        tag.Title = newTitle;

        try {
            await updateTag(tag);
            bsAlert({ type: AlertTypes.Success, content: $Resources.TagManagerAlertUpdateTagBody });
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        }
    }

    async function deleteTags(): Promise<void> {
        if (isBusy.value) return;

        state.isDeleting = true;
        const tagsToDelete = selectedTags.value;

        try {
            await Promise.all(tagsToDelete.map((tag) => deleteTag(tag.TagId)));
            state.tags = state.tags.filter((tag) => !tagsToDelete.includes(tag));
            clearSelectedTags();
            bsAlert({ type: AlertTypes.Success, content: $Resources.TagManagerAlertDeleteTagBody });
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.isDeleting = false;
        }
    }

    async function mergeSelectedTags(targetTag: TagSummary): Promise<void> {
        if (isBusy.value) return;

        state.isMerging = true;
        const sourceTagIds = selectedTags.value.map((tag) => tag.TagId);

        try {
            const mergedTag = await mergeTags(targetTag.TagId, sourceTagIds);
            state.tags = state.tags.filter((tag) => !sourceTagIds.includes(tag.TagId));
            state.tags.push(mergedTag);
            clearSelectedTags();
            bsAlert({ type: AlertTypes.Success, content: $Resources.TagManagerAlertMergeTagsBody });
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.isMerging = false;
        }
    }

    const isBusy = computed(() => state.isLoading || state.isDeleting || state.isCreating || state.isMerging);
</script>

<template>
    <div>
        <h1 class="h2 m-lg-b">{{ $Resources.TagManagerHeading }}</h1>
        <p>{{ $Resources.TagManagerText }}</p>

        <form autocomplete="off" @submit.prevent="submitCreateTag">
            <h2 class="h3">{{ $Resources.TagManagerCreateTagsHeading }}</h2>

            <FormInput
                class="form-group max-300"
                :label="$Resources.TagManagerNewTagTitle"
                :placeholder="$Resources.TagManagerNewTagPlaceholder"
                :modelValue="state.title"
                @update:modelValue="(newVal: string) => (state.title = newVal)">
                <template #button>
                    <button class="btn btn-primary" type="submit" :disabled="state.isCreating">{{ $Resources.TagManagerNewTagButton }}</button>
                </template>
            </FormInput>
        </form>

        <div class="max-700" v-if="state.tags.length > 0">
            <h2 class="h3">{{ $Resources.TagManagerCreatedTagsHeading }}</h2>

            <div class="d-flex flex-wrap align-items-center gap-xs m-sm-b">
                <FormCheckable
                    :label="$Resources.TagManagerEditTagsButton"
                    :switchStyle="SwitchStyle.Standard"
                    :checked="state.isEditMode"
                    @update:modelValue="toggleEditMode"></FormCheckable>

                <BSDropdownButton class="m-auto-l" btnClass="btn-sm" menuClass="dropdown-menu-right" :disabled="selectedTags.length < 2">
                    <template #toggle>
                        {{ $Resources.TagManagerMergeButton }} ({{ selectedTags.length }})
                        <span class="caret m-xs-l"></span>
                    </template>
                    <template #content>
                        <li role="presentation" class="dropdown-header">{{ $Resources.TagManagerMergeLabel }}:</li>
                        <li v-for="tag in selectedTags" :key="tag.TagId" :class="{ disabled: state.isMerging }">
                            <a href="javascript:void(0)" role="button" :aria-disabled="state.isMerging" @click="mergeSelectedTags(tag)">
                                {{ tag.Title }} ({{ tag.UsageCount }})
                            </a>
                        </li>
                    </template>
                </BSDropdownButton>

                <ConfirmButton
                    buttonClasses="btn-warning btn-sm"
                    :buttonText="`${$Resources.TagManagerDeleteTagsButton} (${selectedTags.length})`"
                    :disabled="selectedTags.length < 1 || state.isDeleting"
                    @confirm="deleteTags"
                    undoButton></ConfirmButton>
            </div>

            <ul class="d-flex gap-xs list-unstyled m-xs-b tags-wrap">
                <li v-for="tag in state.tags" :key="tag.TagId">
                    <span v-if="state.isEditMode" class="btn btn-tag btn-tag-edit" style="--btn-font-size: var(--font-size-base)">
                        <span
                            class="editable"
                            contenteditable="true"
                            role="textbox"
                            aria-multiline="false"
                            :aria-label="`${$Resources.TagManagerEditTagsButton}: ${tag.Title}`"
                            @blur="onTitleBlur(tag, $event)"
                            @keydown="disableNewLine">
                            {{ tag.Title }}
                        </span>
                        <span class="m-xs-l">({{ tag.UsageCount }})</span>
                    </span>

                    <button
                        v-else
                        type="button"
                        class="btn btn-tag btn-tag-edit"
                        :class="{ 'btn-is-selected active': isSelected(tag) }"
                        @click="toggleSelectedTag(tag)"
                        style="--btn-font-size: var(--font-size-base)">
                        <span class="editable">{{ tag.Title }}</span>
                        <span class="m-xs-l">({{ tag.UsageCount }})</span>
                    </button>
                </li>
            </ul>
        </div>

        <Loading :show="isBusy" loadingStyle="progress-bar" fixed></Loading>
    </div>
</template>

<style>
    .btn-tag-edit {
        padding: var(--ug-spacing-xs) var(--ug-spacing-sm);
    }

    .btn-tag-edit .editable {
        transition: background-color var(--transition-duration-normal) ease-in-out;
        outline: none;
        border-width: 0 0 1px;
        border-color: rgba(0, 0, 0, 0);
        border-style: solid;
        display: inline-block;
    }

    .btn-tag-edit [contenteditable] {
        border-color: rgba(0, 0, 0, 0.5);
        cursor: text;
    }

    [contenteditable].btn-tag-edit,
    .btn-tag-edit:focus-within [contenteditable] {
        background-color: rgba(0, 0, 0, 0.25);
    }
</style>

